import {
  APP_INITIALIZER,
  ApplicationConfig,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withPreloading,
} from '@angular/router';
import { routes } from './app.routes';
import {
  InterpolatableTranslationObject,
  provideTranslateService,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { defineCustomElements } from '@ctti-dev/gencat/loader';
import { provideAnimations } from '@angular/platform-browser/animations';
import { REQUIRED_INDICATOR_CONFIG } from '@ctti-dev/ng-gencat';

import { LoadingInterceptor } from '@core/interceptors/loading.interceptor';
import { ResponseInterceptor } from '@core/interceptors/response.interceptor';
import { LanguageInterceptor } from '@core/interceptors/language.interceptor';
import { AuthService } from '@core/services/auth.service';
import { AuthExchangeInterceptor } from '@core/interceptors/auth-exchange.interceptor';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import localeEs from '@angular/common/locales/es';
import { registerLocaleData } from '@angular/common';
import { lastValueFrom } from 'rxjs';

defineCustomElements();
registerLocaleData(localeEs);

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (
  http: HttpClient,
) => new TranslateHttpLoader(http, './assets/i18n/', '.json');

function initAuthFactory(auth: AuthService): () => Promise<void> {
  return () => auth.bootstrapAuth();
}

function initTranslationsFactory(
  translate: TranslateService,
): () => Promise<InterpolatableTranslationObject> {
  return () => {
    translate.setDefaultLang('ca');
    return lastValueFrom(translate.use('ca'));
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideHttpClient(
      withInterceptors([
        AuthExchangeInterceptor,
        LoadingInterceptor,
        ResponseInterceptor,
        LanguageInterceptor,
      ]),
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    provideAnimations(),
    {
      provide: REQUIRED_INDICATOR_CONFIG,
      useFactory: (translate: TranslateService) => ({
        requiredText: '',
        optionalText: translate.instant('form.optionalLabel'),
        requiredClassName: '',
        optionalClassName: 'form-text-optional',
        style: { 'margin-left': '4px' },
      }),
      deps: [TranslateService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAuthFactory,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslationsFactory,
      deps: [TranslateService],
      multi: true,
    },
    provideCharts(withDefaultRegisterables()),
  ],
};
